import { useState } from 'react'
import { BBDTag, Horizontal, Texto, MaterialIcon, ManyTag, GraviButton } from '@gravitate-js/excalibrr'
import { Button, Modal, Popconfirm, Popover, Tag, Tooltip } from 'antd'
import type { SupplierConfig } from '../../../../../api/useSupplierReconciliation'
import type { GridApi } from 'ag-grid-community'
import { PopconfirmButton } from '../../../../_SiteWide/PopconfirmButton'

interface MappingStatusCellProps {
  value: string[]
  data: SupplierConfig & {
    terminal_mappings?: {
      total: number
      unmapped: number
    }
    product_mappings?: {
      total: number
      unmapped: number
    }
  }
  api: GridApi
  context: {
    onRemoveMapping: (supplier: SupplierConfig, mappingName: string) => Promise<boolean>
  }
}

export function MappingStatusCell({ value, data, context }: MappingStatusCellProps) {
  const [manageMappingsVisible, setManageMappingsVisible] = useState(false)
  const [editedMappings, setEditedMappings] = useState<string[]>([])
  const [loading, setLoading] = useState(false)

  if (!value) return null

  const { onRemoveMapping } = context

  const handleOpenManageMappings = () => {
    // Filter out any invalid entries and ensure only valid strings are included
    const validMappings = value || []

    setEditedMappings(validMappings)
    setManageMappingsVisible(true)
  }

  const handleRemoveMapping = (mappingName: string) => {
    setEditedMappings(editedMappings.filter((name) => name !== mappingName))
  }

  const handleSaveMappings = async () => {
    try {
      setLoading(true)

      // Find removed mappings
      const removedMappings = value.filter((name) => !editedMappings.includes(name))

      await onRemoveMapping(data, removedMappings)

      // Close the modal
      setManageMappingsVisible(false)
    } catch (error) {
      console.error('Error saving mappings:', error)
    } finally {
      setLoading(false)
    }
  }

  // If no external name mappings, show "No Mappings"
  if (!value || value.length === 0) {
    return (
      <Horizontal gap={8} alignItems='center' className='p-1'>
        <Texto appearance='hint'>No Mappings</Texto>
      </Horizontal>
    )
  }

  const popoverContent = (
    <div>
      <Horizontal gap={8} style={{ flexWrap: 'wrap', marginBottom: '20px' }}>
        {editedMappings.length === 0 ? (
          <Texto appearance='hint'>No mappings available</Texto>
        ) : (
          editedMappings.map((mappingName) => (
            <BBDTag theme2 key={mappingName} closable onClose={() => handleRemoveMapping(mappingName)}>
              {mappingName}
            </BBDTag>
          ))
        )}
      </Horizontal>
      <PopconfirmButton
        showAlert={true}
        onConfirm={handleSaveMappings}
        buttonProps={{
          theme2: true,
          buttonText: 'Save Changes',
          loading: loading,
          style: { marginTop: 8 },
        }}
        popconfirmProps={{
          title: 'Save External Mappings?',
          description: 'This will update the external value mappings for this supplier.',
          okText: 'Yes, Save',
          cancelText: 'Cancel',
        }}
      />
    </div>
  )

  return (
    <div className='mapping-status-cell-wrapper'>
      <Horizontal gap={8} alignItems='center' className='p-1' style={{ flexWrap: 'wrap' }}>
        <Popover
          title='Manage External Mappings'
          trigger='click'
          open={manageMappingsVisible}
          onOpenChange={(visible) => {
            if (visible) {
              handleOpenManageMappings()
            } else {
              setManageMappingsVisible(false)
            }
          }}
          content={popoverContent}
        >
          <Tooltip title='Manage External Mappings'>
            <div style={{ cursor: 'pointer' }}>
              <ManyTag tagItems={value} maxCount={3} />
            </div>
          </Tooltip>
        </Popover>
      </Horizontal>
    </div>
  )
}
